import { useCallback, useEffect, useRef, useState } from "react";
import {
  COMPLETION_ITEM_SOURCE_LABEL,
  COMPLETION_NOTE_TITLE,
  COMPLETION_ROUND_PARENT_LABEL,
  canMarkJournalWebdoc,
  completionNoteDate,
  isCompletionNoteEvidence,
  journalWebdocStatusLine,
  WEBDOC_JOURNAL_LABEL,
  type CompletionNote,
  type CompletionNoteSectionKey,
} from "@care-by-ella/contracts";
import {
  fetchCompletionNote,
  finalizeCompletionNote,
  markCompletionNoteWebdoc,
  saveCompletionNote,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import { hiddenWhenReadOnly } from "./SupportView";

// TES-1060 (TES-1054 point C, Emelie 2026-10-01) — the Kompletteringsanteckning.
//
// Emelie: Kirill sees "den tidigare anteckningen separat", "en egen arbetsyta med rubriken Kompletteringsanteckning", "endast de nya underlagen, deras källa och datum" and "den nya medicinska bedömningen, utfallet, ordinationen och planen".
// "Kopieringsknappen ska endast kopiera den aktuella kompletteringsanteckningen."
//
// The earlier note is shown read-only above the workspace and is never part
// of what is copied: the server compiles the copy from this note's own boxes
// and stores it as the version he can then mark "Införd i Webdoc".

function CompletionNoteWorkspaceView({
  patientId,
  roundId,
  reloadKey,
  onUnauthorized,
}: {
  patientId: string;
  roundId: string;
  // Bumped by the page when something else wrote into the note (the lab
  // assessment), so the boxes show it.
  reloadKey?: number;
  onUnauthorized: () => void;
}) {
  const [note, setNote] = useState<CompletionNote | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<{
    text: string;
    revisionId: string;
    clipboard: boolean;
  } | null>(null);

  // A ref, not a dependency: an inline callback would re-fetch every render.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  const fail = useCallback((e: unknown) => {
    if (e instanceof UnauthorizedError) {
      onUnauthorizedRef.current();
      return;
    }
    setError(errorText(e instanceof Error ? e.message : String(e)));
  }, []);

  const show = useCallback((n: CompletionNote) => {
    setNote(n);
    setDraft(Object.fromEntries(n.sections.map((s) => [s.key, s.content])));
  }, []);

  useEffect(() => {
    let live = true;
    fetchCompletionNote(patientId, roundId)
      .then((n) => {
        if (live) show(n);
      })
      .catch((e) => {
        if (live) fail(e);
      });
    return () => {
      live = false;
    };
  }, [patientId, roundId, reloadKey, show, fail]);

  if (!note) {
    return error ? <p className="text-sm text-danger">{error}</p> : null;
  }

  const changed = note.sections.filter(
    (s) => (draft[s.key] ?? "") !== s.content,
  );

  async function save(): Promise<CompletionNote | null> {
    if (changed.length === 0) return note;
    setBusy(true);
    setError(null);
    try {
      const next = await saveCompletionNote(patientId, roundId, {
        sections: changed.map((s) => ({
          key: s.key as CompletionNoteSectionKey,
          content: draft[s.key] ?? "",
        })),
      });
      show(next);
      return next;
    } catch (e) {
      fail(e);
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!(await save())) return;
    setBusy(true);
    setError(null);
    try {
      const fin = await finalizeCompletionNote(patientId, roundId);
      let clipboard = true;
      try {
        await navigator.clipboard.writeText(fin.text);
      } catch {
        // No clipboard permission: the text is shown below to copy by hand.
        clipboard = false;
      }
      setCopied({ text: fin.text, revisionId: fin.revisionId, clipboard });
      setNote((n) => (n ? { ...n, webdoc: fin.webdoc } : n));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function mark() {
    const revisionId = note?.webdoc.latestFinal?.revisionId;
    if (!revisionId) return;
    setBusy(true);
    setError(null);
    try {
      const webdoc = await markCompletionNoteWebdoc(
        patientId,
        roundId,
        revisionId,
      );
      setNote((n) => (n ? { ...n, webdoc } : n));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  const evidence = note.round.items.filter(
    (i) => i.status === "done" || i.status === "waived",
  );
  const webdocLine = journalWebdocStatusLine(note.webdoc);

  return (
    <section
      className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md"
      data-testid="completion-note"
    >
      <div>
        <h2 className="font-display text-lg">
          {COMPLETION_NOTE_TITLE} {note.noteDate}
        </h2>
        <p className="text-xs text-textSecondary">
          Komplettering{" "}
          {COMPLETION_ROUND_PARENT_LABEL[note.parentKind] ?? note.parentKind},
          begärd {completionNoteDate(note.round.requestedAt)}
        </p>
      </div>

      <div data-testid="completion-note-earlier">
        <h3 className="text-sm font-semibold mb-xs">
          Tidigare journalanteckning
          {note.earlierNote?.locked ? " (låst)" : ""}
        </h3>
        {note.earlierNote ? (
          <>
            <pre className="whitespace-pre-wrap rounded-sm border border-surfaceMuted bg-surfaceMuted/40 p-sm text-xs text-textSecondary max-h-64 overflow-y-auto">
              {note.earlierNote.text}
            </pre>
            {journalWebdocStatusLine(note.earlierNote.webdoc) ? (
              <p className="mt-xs text-xs text-textSecondary">
                {journalWebdocStatusLine(note.earlierNote.webdoc)}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-xs text-textSecondary">
            Ingen tidigare färdigställd journalanteckning finns i Ella.
          </p>
        )}
      </div>

      <div data-testid="completion-note-evidence">
        <h3 className="text-sm font-semibold mb-xs">Nya underlag</h3>
        {evidence.length === 0 ? (
          <p className="text-xs text-textSecondary">
            Inga nya underlag har kommit in ännu.
          </p>
        ) : (
          <ul className="flex flex-col gap-xs text-xs">
            {evidence.map((i) => (
              <li key={i.id} className="flex flex-wrap gap-x-sm">
                <span className="text-textPrimary">{i.label}</span>
                <span className="text-textSecondary">
                  {[
                    i.source ? COMPLETION_ITEM_SOURCE_LABEL[i.source] : null,
                    i.doneAt ? completionNoteDate(i.doneAt) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  {/* Ö-136 — her own report is not a result, and is not
                      journaled as one. TES-1095 (rule 8) — nor is a
                      payment. */}
                  {isCompletionNoteEvidence(i)
                    ? ""
                    : i.source === "payment"
                      ? " · journalförs inte"
                      : " · patientens egen uppgift, inget provsvar"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-sm">
        {note.sections.map((s) => (
          <label key={s.key} className="flex flex-col gap-xs text-sm">
            <span className="font-semibold">{s.heading}</span>
            <textarea
              value={draft[s.key] ?? ""}
              onChange={(e) =>
                setDraft((d) => ({ ...d, [s.key]: e.target.value }))
              }
              disabled={busy}
              rows={s.key === "nya_underlag" ? 4 : 3}
              className="rounded-sm border border-surfaceMuted bg-surface p-sm text-sm"
            />
          </label>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-sm">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy || changed.length === 0}
          className="rounded-md border border-surfaceMuted px-md py-sm text-sm disabled:opacity-50"
        >
          Spara utkast
        </button>
        <button
          type="button"
          onClick={() => void copy()}
          disabled={busy}
          className="rounded-md bg-brand px-md py-sm text-sm text-textOnBrand hover:opacity-90 disabled:opacity-50"
        >
          Kopiera kompletteringsanteckningen
        </button>
        {canMarkJournalWebdoc(note.webdoc) ? (
          <button
            type="button"
            onClick={() => void mark()}
            disabled={busy}
            className="rounded-md border border-brand px-md py-sm text-sm text-brand disabled:opacity-50"
          >
            {WEBDOC_JOURNAL_LABEL}
          </button>
        ) : null}
      </div>
      {webdocLine ? (
        <p className="text-xs text-textSecondary">{webdocLine}</p>
      ) : null}
      {copied ? (
        <div className="flex flex-col gap-xs">
          <p className="text-xs text-textSecondary">
            {copied.clipboard
              ? "Kopierad. Endast kompletteringsanteckningen kopierades."
              : "Kunde inte kopiera automatiskt. Markera texten och kopiera den."}
          </p>
          <pre className="whitespace-pre-wrap rounded-sm border border-surfaceMuted p-sm text-xs">
            {copied.text}
          </pre>
        </div>
      ) : null}
      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </section>
  );
}

// The doctor's own work: nothing to show in a read-only support view.
export const CompletionNoteWorkspace = hiddenWhenReadOnly(
  CompletionNoteWorkspaceView,
);
